import { useEffect } from 'react';

import { useQueryClient } from '@tanstack/react-query';

import { dashboardQueryKeys } from '@/features/Dashboard/api/utils';

const CHANNEL_NAME = 'gx_dashboard_sync';

/**
 * Notifies other browser tabs that a dashboard mutation has occurred.
 * Opens a short-lived BroadcastChannel, posts the message, then closes it.
 * The current tab refetches via its own mutation callback.
 */
export const broadcastDashboardMutation = (): void => {
  const channel = new BroadcastChannel(CHANNEL_NAME);
  channel.postMessage(null);
  channel.close();
};

/**
 * Listens for dashboard mutations broadcast from other tabs and refetches
 * the dashboard query so this tab stays in sync automatically.
 *
 * Call once in AppLayout (or any persistent authenticated component).
 */
export const useDashboardSync = (): void => {
  const queryClient = useQueryClient();

  useEffect(() => {
    const channel = new BroadcastChannel(CHANNEL_NAME);

    channel.onmessage = () => {
      queryClient.refetchQueries({ queryKey: dashboardQueryKeys.all }).catch(() => undefined);
    };

    return () => {
      channel.close();
    };
  }, [queryClient]);
};
